import {
  RealtimeRole,
  type RealtimeMember as RealtimeMemberWithUntypedState,
  type RealtimeCredentials,
  type RealtimeState as UntypedRealtimeState,
  type RealtimeEvent,
  type RealtimeIdentity,
} from '@doxyme/realtime';
import { type Vendor } from '../../webrtc/media';
import { type AvatarProperties } from '@doxyme/ui-foundation/components/avatar';
import { type NetworkQuality } from '@doxyme/ui/components';
import {
  type Browser,
  type BrowserType,
  type OS,
} from '@doxyme/ui-foundation/hooks';
import { type CallStartSoundOptions } from '../../components/settings/preferences';
import {
  type ConnectionTestError,
  type ConnectionTestResult,
} from '../../hooks/useConnectionTest';
import { type Manifest } from '@doxyme/extensions-graphql-types';

export { RealtimeRole, type RealtimeEvent, type RealtimeIdentity };

/**
 * Typed realtime state
 */
export type RealtimeState = UntypedRealtimeState & {
  /**
   * Preferred name if set through settings
   * OR title (Dr, Mr, Mrs, etc.) + last name if title is set
   * OR first name + last name
   */
  username?: string;
  userLabel?: string;
  status?: RealtimeMemberStatus;
  mobileBrowser?: boolean;
  onlyChat?: boolean;
  checkedInAt?: number;
  legacyIdentityId?: string;
  privateData?: RealtimeStatePrivateData;
  system?: RealtimeSystemInfo;
  videoState?: 'blocked' | 'off' | 'on';
  audioState?: 'blocked' | 'off' | 'on';
  /**
   * Whether photo-at-check-in was enabled for this patient at their check-in.
   */
  checkInPhotoCaptureEnabled?: boolean;
  networkQuality?: NetworkQuality;
  flagCode?: AvatarProperties['flagCode'];
  inviteId?: string;
  sessionId?: string;
  dbSessionId?: string;
  sessionOwnershipToken?: string;
  callStartSound?: CallStartSoundOptions | null;
  preCallTest?: {
    isRunning: boolean;
    result?: ConnectionTestResult;
    error?: ConnectionTestError;
  };
  transferredBy?: { userId: string; username: string };
  inCallWithProviderName?: string;
  inCallWithProviderId?: string;
};

export type RealtimeStatePrivateData = {
  ip: string;
  location: {
    country_code?: string;
    country_name?: string;
    region_name?: string;
  };
};

export type RealtimeSystemInfo = {
  os?: OS;
  osVersion?: string;
  browser?: Browser;
  browserVersion?: string;
  platform?: BrowserType;
};

/**
 * Realtime member with typed state, redefines prop
 * {state} - typed realtime state
 */
export type RealtimeMember = RealtimeMemberWithUntypedState & {
  state: RealtimeState;
  /**
   * To be set for the mock call experience patients. No a real realtime member.
   */
  isTestPatient?: boolean;
};

export enum RealtimeMemberStatus {
  Online = 'available',
  Offline = 'offline',
  /**
   * The provider paused the call, and the patient is on hold
   */
  OnHold = 'onhold',
  /**
   * "video" status is used in OG to indicate the user is on a call.
   * Since we are using realtime for both OG and V2 (this app),
   * we must use the exact same string values.
   * Otherwise, 2 different sets of statuses for OG and V2
   * could lead to unforeseen issues.
   */
  Busy = 'video',
}

export enum RealtimeMessageType {
  LegacyRemovedFromWaitingRoom = 'global:removedFromWaitingRoom',
  RemovedFromWaitingRoom = 'removedFromWaitingRoom',
  LegacyCallStart = 'phone:startv2',
  CallStart = 'callStart',
  CallRestart = 'callRestart',
  CallHold = 'callHold',
  CallResume = 'callResume',
  CallRestartIsReady = 'callRestartIsReady',
  GroupCallParticipantLeft = 'GroupCallParticipantRemoved',
  ParticipantMuted = 'participantMuted',
  ChatStart = 'chatStart',
  ChatMessage = 'chatMessage',
  ChatIsTyping = 'chatIsTyping',
  MessageAck = 'messageAck',
  ScreenshareRequest = 'screenshareRequest',
  FileTransferRequest = 'fileTransferRequest',
  // Extensions related
  ExtensionRoomCommand = 'extensionRoomCommand',
  ExtensionCallCommand = 'extensionCallCommand',
  ExtensionRegistryUpdate = 'extensionRegistryUpdate',
  ExtensionSideloadSync = 'extensionSideloadSync',
  TranscriptionStatus = 'transcriptionStatus',
  FileServiceUpload = 'fileServiceUpload',
  // /Extensions related
  RemoveParticipantsAfterCallHangUp = 'removeParticipantsAfterCallHangUp',
  InviteViaTextMessage = 'phoneConfirmation',
  PhotoCapture = 'photoCapture',
  RemoveOnHoldParticipants = 'removeOnHoldParticipants',
  ManualVideoQuality = 'manualVideoQuality',
  LinkProviderStatus = 'linkProviderStatus',

  // Patient transfer
  PatientTransfer = 'patientTransfer',
  PatientTransferSuccessConfirmation = 'patientTransferSuccessConfirmation',
}

export type RealtimeMessage<T = unknown> = {
  messageType: RealtimeMessageType;
  payload?: T;
  messageId?: string;
  requestAck?: boolean;
};

export type LegacyMessage = {
  message: {
    action: string;
    componentName: string;
    content: unknown;
    messageId?: string;
    requestAck?: boolean;
  };
};

export type ConnectPayload = {
  role: RealtimeRole;
  roomIds: string[];
  credentials?: RealtimeCredentials;
  state?: RealtimeState;
};

export type JoinRoomsPayload = ConnectPayload;

export type RealtimeSlice = {
  state?: RealtimeState;
  membersMap: Record<string, RealtimeMember>; // contains all members that were ever joined
  tenantMembersMap: Record<string, RealtimeMember>; // contains all tenant members that were ever joined
  joinedMembersIds: string[]; // contains ids of currently joined members
  removedMembersIds: string[];
  identity?: RealtimeIdentity;
  isJoined: boolean;
  isLoading: boolean;
  picture?: string;
  ackRequests: Record<string, number>;
};

export type RealtimeSendMessageProps = {
  id: string;
  message: RealtimeMessage | LegacyMessage;
};

export type RealtimeSendMessageManyProps = {
  ids: string[];
  message: RealtimeMessage | LegacyMessage;
};

export type RealtimeSendMessageToAllIdentitiesProps = {
  chatUserId: string;
  message: RealtimeMessage | LegacyMessage;
};

export type RealtimeOnProps = {
  callback: (message: RealtimeMessage, event: RealtimeEvent) => void;
};

export type RealtimeOnMessageProps = RealtimeOnProps & {
  messageType: RealtimeMessageType;
};

export type ChatUserId<T> = T extends RealtimeIdentity ? string : undefined;

export type CallStartMessagePayload = {
  sessionId: string;
  joinToken: string;
  vendor?: Vendor;
  joinedParticipantsIds?: string[];
  isAudioCall?: boolean;
  startCallTime: number;
  dbSessionId?: string;
  providerUserId?: string;
  providerName?: string;
};

export type CallHoldMessagePayload = {
  sessionOwnershipToken: string;
};

export type ExtensionSideloadSyncPayload = {
  sideloads: Array<{
    baseUrl: string;
    manifest: Manifest;
  }>;
};
